/*
defines link interactions
*/

import LinkView from 'view/link';
import GraphController from './graph';

/**
 * defines a class containing handlers for all links.
 * there is 1 instance of this class for all links.
 */
export default class LinkController {
    /**
     * makes a new LinkController
     * @param {GraphController} graphController the controller for the graph
     * @param {Graph} model the graph model to use
     * @param {GraphView} view the graph view to use
     */
    constructor(graphController, model, view) {
        this.g = model;
        this.view = view;
        this.graphController = graphController;
    }

    /**
     * defines and attaches all link event handlers
     */
    initHandlers() {
        this.bindHandlers();
    }

    /**
     * attaches all handlers to all links
     */
    bindHandlers() {
        this.hoverLinkHandler();
        this.clickLinkHandler();
        this.useInputHandler();
    }

    /**
     * binds the hover handler
     */
    hoverLinkHandler() {
        LinkView.transparentLineSelection
            .on('mouseover', (linkView) => this.onMouseOver(linkView))
            .on('mouseout', (linkView) => this.onMouseOut(linkView));
    }

    /**
     * binds the click handler
     */
    clickLinkHandler() {
        LinkView.transparentLineSelection
            .on('click.all', (linkView) => this.onClick(linkView));
    }

    /**
     * binds the user input handler
     */
    useInputHandler() {
        LinkView.inputBoxSelection
            .on('keydown.link', () => this.onKeyDown());
    }

    /**
     * called when the given link is hovered on
     * effect: makes it more opaque
     * @param {LinkView} linkView the hovered link
     */
    onMouseOver(linkView) {
        linkView.reinforceLink();
    };

    /**
     * called when the given link is unhovered
     */
    onMouseOut(linkView) {
        // nothing
    };

    /**
     * called when the given link is clicked
     * @param {LinkView} linkView the clicked link
     */
    onClick(linkView) {
        if (this.graphController.state.is('DELETING')) {
            this.graphController.removeLink(linkView.id);
            this.graphController.state.set('NONE');
        } else {
            this.view.resetLinkForce();
            this.changeLinkForce(linkView.id, 350, 1);

            linkView.showBox();
            LinkView.inputBoxSelection.node().focus();
            LinkView.inputBoxSelection.node().select();
            LinkView.inputBoxSelection.node().value = "";
            this.graphController.state.set('CONNECTING');
        }
    };

    /**
     * when a key is pressed inside the input box
     */
    onKeyDown() {
        if (this.graphController.state.is('CONNECTING')) {
            let linkId = LinkView.inputBoxContainerSelection.attr('data-link');
            let link = this.view.getLink(linkId);
            if (d3.event.key === 'Enter') {
                this.resetLinkForce();
                link.hideBox();
                let query = LinkView.inputBoxSelection.node().value;
                this.graphController.interleave(linkId, query.toLowerCase());
                this.graphController.state.set('NONE');
            } else if (d3.event.key === 'Escape') {
                this.resetLinkForce();
                link.hideBox();
                this.graphController.state.set('NONE');
            }
        }
    };

    /**
     * changes the physical distance and strength of the connection of the given link
     * @param {string} id the id of the link to change
     * @param {number} distance new distance in px between the two nodes
     * @param {number} strength new strength of the link
     */
    changeLinkForce(id, distance, strength) {
        this.view.changeLinkForce(id, distance, strength);
    }

    /**
     * resets all link distances and strengths
     */
    resetLinkForce() {
        this.view.resetLinkForce();
        this.view.redefineLinkForce();
    }
}
